import React from "react";
import { useTranslation } from "react-i18next";
import { AlignLeft, List, Layers } from "lucide-react";
import { cn } from "@/lib/utils";

export type ScriptView = "full" | "pointForm" | "both";

// The guest (or host) picks Detailed or Point form for themselves; the choice is
// remembered on this device. The order-time script_type is only the starting view.
const SCRIPT_VIEW_STORAGE_KEY = "mm_script_view";

export const readSavedScriptView = (): ScriptView | null => {
  try {
    const v = window.localStorage.getItem(SCRIPT_VIEW_STORAGE_KEY);
    return v === "full" || v === "pointForm" || v === "both" ? v : null;
  } catch {
    return null;
  }
};

export const saveScriptView = (v: ScriptView) => {
  try {
    window.localStorage.setItem(SCRIPT_VIEW_STORAGE_KEY, v);
  } catch {
    /* private window or blocked storage: the choice just is not remembered */
  }
};

// Starting view: the saved choice when it is allowed for this order, else the order-time
// setting. 'both' (prose with bullets stacked underneath, the long print) is only valid for
// orders bought as 'both'.
export const initialScriptView = (orderSetting: ScriptView): ScriptView => {
  const saved = readSavedScriptView();
  return saved && (saved !== "both" || orderSetting === "both") ? saved : orderSetting;
};

interface ScriptViewToggleProps {
  value: ScriptView;
  onChange: (value: ScriptView) => void;
  // 'both' (prose with bullets stacked underneath) is only offered to orders bought
  // as 'both'; everyone else chooses between Detailed and Point form.
  allowBoth?: boolean;
  // Stick to the top of the screen while the sheet scrolls (guest page).
  sticky?: boolean;
  className?: string;
}

// Tabs that sit on top of the sheet they change: an icon and the name each.
const ScriptViewToggle: React.FC<ScriptViewToggleProps> = ({ value, onChange, allowBoth = false, sticky = false, className }) => {
  const { t } = useTranslation();
  const tabs: { id: ScriptView; label: string; Icon: typeof List }[] = [
    { id: "full", label: t("scriptView.detailed"), Icon: AlignLeft },
    { id: "pointForm", label: t("scriptView.pointForm"), Icon: List },
    ...(allowBoth ? [{ id: "both" as ScriptView, label: t("scriptView.both"), Icon: Layers }] : []),
  ];
  return (
    <div
      role="group"
      aria-label={t("scriptView.label")}
      className={cn(
        "grid border-b bg-card print:hidden",
        allowBoth ? "grid-cols-3" : "grid-cols-2",
        sticky && "sticky top-0 z-20",
        className,
      )}
    >
      {tabs.map(({ id, label, Icon }) => {
        const active = value === id;
        return (
          <button
            key={id}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(id)}
            className={cn(
              "-mb-px flex items-center justify-center border-b-[3px] px-2 py-3 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset",
              active
                ? "border-primary bg-gradient-to-b from-transparent to-primary/15 text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground",
            )}
          >
            <span className="flex items-center gap-1.5 text-sm font-semibold">
              <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
              {label}
            </span>
          </button>
        );
      })}
    </div>
  );
};

export default ScriptViewToggle;
